// The game's side of the online API: who you are (a name and a secret key kept in this browser),
// hiscores and player maps. Everything here is optional: if the API can't be reached the game
// plays on with local personal bests.

import { toBase64Url } from './levelCode';

export const API_URL: string =
  (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/+$/, '') ?? 'https://api.bhop.lasboi.com';

const KEY = 'bhop-web-player';

export interface Identity {
  id: number;
  name: string;
  /** Secret key that owns the name (also the recovery key). */
  token: string;
}

export class OnlineError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly data: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

export interface TimeEntry {
  rank: number;
  name: string;
  ticks: number;
  verified: boolean;
  jumps: number;
  sync: number;
  date: number;
}

export interface Board {
  top: TimeEntry[];
  total: number;
  me: { ticks: number; rank: number } | null;
}

export interface SubmitResult {
  ticks: number;
  improved: boolean;
  verified: boolean;
  rank: number | null;
  best: number;
}

export interface PlayerMap {
  code: string;
  title: string;
  author: string;
  pieces: number;
  downloads: number;
  rating: number | null;
  ratings: number;
  created: number;
  myRating: number | null;
  favorite: boolean;
  mine: boolean;
}

export type MapSort = 'top' | 'downloads' | 'new' | 'favorites' | 'mine';

function loadIdentity(): Identity | null {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? 'null') as Partial<Identity> | null;
    if (v && typeof v.token === 'string' && typeof v.name === 'string' && typeof v.id === 'number')
      return { id: v.id, name: v.name, token: v.token };
  } catch {
    // Storage blocked.
  }
  return null;
}

class OnlineClient {
  private ident: Identity | null = loadIdentity();
  private readonly listeners = new Set<() => void>();

  get me(): Identity | null {
    return this.ident;
  }

  /** Called when the name changes (signed up, renamed, signed out). */
  onChange(fn: () => void) {
    this.listeners.add(fn);
  }

  private setIdentity(i: Identity | null) {
    this.ident = i;
    try {
      if (i) localStorage.setItem(KEY, JSON.stringify(i));
      else localStorage.removeItem(KEY);
    } catch {
      // Not remembered.
    }
    for (const fn of this.listeners) fn();
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    token = this.ident?.token,
  ): Promise<T> {
    let res: Response;
    try {
      res = await fetch(API_URL + path, {
        method,
        headers: {
          ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
          ...(token ? { Authorization: 'Bearer ' + token } : {}),
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: AbortSignal.timeout(20_000),
      });
    } catch {
      throw new OnlineError(0, "Can't reach the hiscore server right now.");
    }
    let data: Record<string, unknown> = {};
    try {
      data = (await res.json()) as Record<string, unknown>;
    } catch {
      // Not JSON.
    }
    if (!res.ok) {
      // The key no longer works (player removed): forget it.
      if (res.status === 401 && token && token === this.ident?.token) this.setIdentity(null);
      throw new OnlineError(res.status, String(data.error ?? `Server error ${res.status}`), data);
    }
    return data as T;
  }

  async signUp(name: string): Promise<Identity> {
    const r = await this.request<{ token: string; player: { id: number; name: string } }>(
      'POST',
      '/players',
      { name },
      undefined,
    );
    const i = { ...r.player, token: r.token };
    this.setIdentity(i);
    return i;
  }

  async rename(name: string): Promise<void> {
    const r = await this.request<{ player: { id: number; name: string } }>('PATCH', '/me', { name });
    if (this.ident) this.setIdentity({ ...this.ident, name: r.player.name });
  }

  /** Sign in with a recovery key (the key from another browser). */
  async useKey(token: string): Promise<Identity> {
    const t = token.trim();
    const r = await this.request<{ player: { id: number; name: string } }>('GET', '/me', undefined, t);
    const i = { ...r.player, token: t };
    this.setIdentity(i);
    return i;
  }

  /** Check the saved name is still the server's (renamed elsewhere, or removed). */
  async refresh(): Promise<void> {
    if (!this.ident) return;
    const r = await this.request<{ player: { id: number; name: string } }>('GET', '/me');
    if (this.ident && r.player.name !== this.ident.name)
      this.setIdentity({ ...this.ident, name: r.player.name });
  }

  signOut() {
    this.setIdentity(null);
  }

  board(board: string, rules: string, limit = 10): Promise<Board> {
    const q = new URLSearchParams({ board, rules, limit: String(limit) });
    return this.request<Board>('GET', '/times?' + q);
  }

  /** The board's fastest checked run (name, time and replay) for the replay bot. */
  topReplay(
    board: string,
    rules: string,
  ): Promise<{ bot: { name: string; ticks: number; replay: string } | null }> {
    const q = new URLSearchParams({ board, rules });
    return this.request('GET', '/replay?' + q);
  }

  submitChecked(board: string, replay: Uint8Array): Promise<SubmitResult> {
    return this.request<SubmitResult>('POST', '/times', { board, replay: toBase64Url(replay) });
  }

  submitUnchecked(
    board: string,
    rules: string,
    ticks: number,
    jumps: number,
    sync: number,
    replay: Uint8Array,
  ): Promise<SubmitResult> {
    return this.request<SubmitResult>('POST', '/times', {
      board,
      rules,
      ticks,
      jumps,
      sync,
      replay: toBase64Url(replay),
    });
  }

  maps(sort: MapSort, query: string, page: number): Promise<{ maps: PlayerMap[]; more: boolean }> {
    const q = new URLSearchParams({ sort, q: query, page: String(page) });
    return this.request('GET', '/maps?' + q);
  }

  map(code: string): Promise<{ map: PlayerMap; level: string }> {
    return this.request('GET', '/maps/' + encodeURIComponent(code));
  }

  publish(title: string, level: Uint8Array, replay: Uint8Array): Promise<{ code: string; ticks: number }> {
    return this.request('POST', '/maps', { title, level: toBase64Url(level), replay: toBase64Url(replay) });
  }

  rate(code: string, stars: number): Promise<{ map: PlayerMap; myRating: number | null }> {
    return this.request('PUT', `/maps/${code}/rating`, { stars });
  }

  favorite(code: string, on: boolean): Promise<{ favorite: boolean }> {
    return this.request('PUT', `/maps/${code}/favorite`, { on });
  }

  deleteMap(code: string): Promise<{ deleted: boolean }> {
    return this.request('DELETE', '/maps/' + code);
  }
}

export const online = new OnlineClient();
